//================= [JS] ======================================
import {onBeforeUnmount, ref, watch, type Ref} from 'vue';

/** Above this level someone counts as speaking (0–1, RMS of the waveform). */
export const SPEAKING_THRESHOLD = 0.06;

/**
 * T00VLGPYIV5X — how loud a stream is right now, 0 to 1, for the microphone
 * meter in the preview and the speaking glow in the call. Follows the stream:
 * a new or removed stream restarts or stops the measurement. Without Web
 * Audio (or without an audio track) the level stays 0.
 */
export function useAudioLevel(stream: Ref<MediaStream | null>) {
  const level = ref(0);
  let context: AudioContext | null = null;
  let frame = 0;

  function stop(): void {
    if (frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
    void context?.close().catch(() => undefined);
    context = null;
    level.value = 0;
  }

  function start(current: MediaStream | null): void {
    stop();
    const AudioContextClass = typeof window !== 'undefined' ? window.AudioContext : undefined;
    if (!current || current.getAudioTracks().length === 0 || !AudioContextClass) {
      return;
    }
    context = new AudioContextClass();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    context.createMediaStreamSource(current).connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) {
        const centered = (sample - 128) / 128;
        sum += centered * centered;
      }
      // Smooth a little so the meter does not flicker.
      level.value = level.value * 0.6 + Math.sqrt(sum / samples.length) * 0.4;
      frame = requestAnimationFrame(tick);
    };
    tick();
  }

  watch(stream, start, {immediate: true});
  onBeforeUnmount(stop);

  return {level, stop};
}
